import React, { useState } from 'react';
import {
  TrendingUp,
  Sliders,
  ShieldCheck,
  CheckCircle2,
  Lock,
  AlertTriangle,
  Award,
  DollarSign,
  BarChart3,
  Search
} from 'lucide-react';
import { ChannelAttributionItem, AnalyticsExperimentItem } from '../../types';
import { analyticsEngine } from '../../services/analyticsEngine';

export const AttributionExperimentationView: React.FC = () => {
  const [channels] = useState<ChannelAttributionItem[]>(analyticsEngine.getAttributionItems());
  const [experiments] = useState<AnalyticsExperimentItem[]>(analyticsEngine.getExperiments());

  const mathExpCheck = analyticsEngine.runFinancialMathIsNeverExperimentCheck();

  return (
    <div className="space-y-8 pb-12">
      {/* Hero Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold border border-purple-500/30">
          <TrendingUp className="w-3.5 h-3.5" />
          Acquisition Attribution & A/B Experimentation Platform
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Acquisition Attribution & Controlled A/B Testing</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Evaluate organic search, referral, and email channel contribution while running controlled UX experiments with mathematical correctness guardrails.
        </p>
      </div>

      {/* Financial Math Experiment Prohibition Banner */}
      <div className="bg-purple-950/90 text-white p-6 rounded-2xl border border-purple-800 shadow-md space-y-3">
        <div className="flex items-center gap-2 border-b border-purple-800 pb-2">
          <Lock className="w-4 h-4 text-emerald-400" />
          <h3 className="font-bold text-sm">Financial Calculation Mathematical Correctness Assurance</h3>
        </div>
        <p className="text-purple-100 text-xs leading-relaxed">
          {mathExpCheck.message} YamooPay strictly prohibits A/B testing tax algorithms, loan formulas, or currency rate conversion mathematics. All experiments are restricted to UX wording, layout, and CTA placement.
        </p>
      </div>

      {/* Channel Attribution Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-5">
        <div className="border-b border-slate-100 pb-4 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-purple-600 uppercase tracking-wider">
              Multi-Touch Acquisition Channels
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              Acquisition & Attribution Models
            </h2>
          </div>

          <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold">
            Organic Search #1 Driving Channel
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                <th className="py-2.5 px-3">Acquisition Channel</th>
                <th className="py-2.5 px-3">Qualified Visitors</th>
                <th className="py-2.5 px-3">Successful Calculations</th>
                <th className="py-2.5 px-3">Signups</th>
                <th className="py-2.5 px-3">Affiliate Clicks</th>
                <th className="py-2.5 px-3">Gross Revenue</th>
                <th className="py-2.5 px-3">First Touch %</th>
                <th className="py-2.5 px-3">Last Non-Direct %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-mono text-slate-700">
              {channels.map((ch) => (
                <tr key={ch.channel} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-bold text-slate-900">{ch.channel}</td>
                  <td className="py-2.5 px-3">{ch.qualifiedVisitorsCount.toLocaleString()}</td>
                  <td className="py-2.5 px-3 font-bold text-emerald-700">{ch.successfulCalculationsCount.toLocaleString()}</td>
                  <td className="py-2.5 px-3 font-bold text-purple-700">{ch.signupsCount.toLocaleString()}</td>
                  <td className="py-2.5 px-3 font-bold text-blue-700">{ch.affiliateClicksCount.toLocaleString()}</td>
                  <td className="py-2.5 px-3 font-extrabold text-slate-900">${ch.grossRevenueUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="py-2.5 px-3 font-bold text-purple-700">{ch.firstTouchSharePct}%</td>
                  <td className="py-2.5 px-3 font-bold text-emerald-700">{ch.lastNonDirectSharePct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Active Experiments Registry */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="border-b border-slate-100 pb-4">
          <h2 className="text-xl font-bold text-slate-900">Active A/B Experiments Registry</h2>
          <p className="text-xs text-slate-500 mt-1">
            Feature flag experiments evaluating conversion uplift with guardrail metrics.
          </p>
        </div>

        <div className="space-y-4">
          {experiments.map((exp) => (
            <div key={exp.id} className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold bg-slate-900 text-white px-2 py-0.5 rounded">
                    {exp.experimentKey}
                  </span>
                  <h3 className="font-bold text-slate-900 text-sm">{exp.title}</h3>
                </div>

                <span className="px-2.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-bold">
                  {exp.status}
                </span>
              </div>

              <p className="text-xs text-slate-600 font-medium">{exp.hypothesis}</p>

              <div className="p-3 bg-white rounded-lg border border-slate-200 text-xs space-y-1.5">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Guardrail Metrics</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {exp.guardrails.map((g, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 rounded border border-slate-100 text-[11px]">
                      <div className="font-bold text-slate-800">{g.metricName}</div>
                      <div className="text-slate-500">Threshold: {g.threshold} | Current: <strong className="text-emerald-700 font-bold">{g.currentValue}</strong></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between text-[11px] font-mono text-slate-500 pt-1">
                <span>Sample Size: <strong>{exp.currentSampleSize.toLocaleString()}</strong> / {exp.minSampleSize.toLocaleString()}</span>
                <span className="text-emerald-700 font-extrabold">Primary Uplift: {exp.primaryMetricDeltaPct > 0 ? `+${exp.primaryMetricDeltaPct}%` : `${exp.primaryMetricDeltaPct}%`}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
